import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { OutingStatus } from "@alm/validation";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { RowActions } from "@/components/app/patterns/row-actions";
import { SearchField } from "@/components/app/patterns/search-field";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { LogOut } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { optionalOneOf, searchPatch, optionalSearch } from "@/lib/search";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZoneFor } from "@/lib/facility/zone";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { HeadcountTiles } from "./-components/headcount-tiles";
import { outingColumns, type OutingRow } from "./-components/outing-columns";
import { OutingSignInDialog } from "./-components/outing-sign-in-dialog";
import { OutingSignOutDialog } from "./-components/outing-sign-out-dialog";
import { VisitorNav } from "./-components/visitor-nav";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/residents")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: "returned" | "all"; q?: string } => ({
    status: optionalOneOf(["returned", "all"], search.status),
    q: optionalSearch(search.q),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: ResidentsOutPage,
});

const LIMIT = 200;

const STATUSES: ReadonlyArray<{ value: OutingStatus; label: string }> = [
  { value: "out", label: "Out now" },
  { value: "returned", label: "Back" },
  { value: "all", label: "All" },
];

type Dialog = { kind: "sign-out" } | { kind: "sign-in"; row: OutingRow } | null;

/** Residents who have gone out and come back: signed out at the kiosk, with Grace, or by staff. */
function ResidentsOutPage() {
  const { facilityId } = useFacility();
  const pinned = facilityId === "all" ? null : facilityId;
  const canManage = useCan("visitor:manage", pinned);
  const zoneFor = useZoneFor();
  const { status: statusParam, q: needle } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const status: OutingStatus = statusParam ?? "out";
  const setStatus = (next: OutingStatus) => patch({ status: next === "out" ? undefined : next });
  const { text: search, change: setSearch } = useSearchParamText(needle, (q) => patch({ q }));
  const [dialog, setDialog] = useState<Dialog>(null);
  const list = useQuery({
    ...orpc.visitors.outings.list.queryOptions({
      input: { facilityId, status, search: needle, limit: LIMIT, offset: 0 },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Residents out"
        count={list.isSuccess ? total : undefined}
        meta="Residents sign out at the kiosk or with Grace, and back in when they come home. Staff can do either for them."
        actions={
          canManage ? (
            <Button onClick={() => setDialog({ kind: "sign-out" })}>
              <LogOut data-icon="inline-start" aria-hidden="true" />
              Sign a resident out
            </Button>
          ) : undefined
        }
      />
      <VisitorNav />
      <HeadcountTiles facilityId={facilityId} />
      {list.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the residents who are out."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={rows}
          columns={outingColumns(zoneFor, true)}
          noun="outings"
          rowId={(row) => row.id}
          pending={list.isPending}
          renderToolbar={() => (
            <ListToolbar
              search={
                <SearchField
                  value={search}
                  onChange={setSearch}
                  placeholder="Search residents"
                  label="Search residents"
                />
              }
              chips={
                <fieldset className="flex min-w-0 flex-wrap gap-1.5">
                  <legend className="sr-only">Filter by status</legend>
                  {STATUSES.map((option) => (
                    <FilterChip
                      key={option.value}
                      selected={status === option.value}
                      onClick={() => setStatus(option.value)}
                    >
                      {option.label}
                    </FilterChip>
                  ))}
                </fieldset>
              }
            />
          )}
          renderRowActions={(row) => (
            <RowActions
              label={`${row.residentName}'s outing`}
              modal={false}
              primary={
                canManage &&
                !row.returnedAt && {
                  label: "Sign back in",
                  onSelect: () => setDialog({ kind: "sign-in", row }),
                }
              }
              items={[
                {
                  label: "Open chart",
                  link: {
                    to: "/residents/$residentId/outings",
                    params: { residentId: row.residentId },
                  },
                },
              ]}
            />
          )}
          empty={
            <EmptyState
              title={
                status === "out" ? "Every resident is in the building" : "No outings recorded yet"
              }
              description="Residents appear here when they sign out at the kiosk, with Grace, or when staff sign them out."
            />
          }
        />
      )}
      {total > rows.length ? (
        <p className="text-muted-foreground text-sm">
          Showing the latest {rows.length} of {total}. Search a name to narrow it.
        </p>
      ) : null}
      {dialog?.kind === "sign-out" ? (
        <OutingSignOutDialog facilityId={pinned} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "sign-in" ? (
        <OutingSignInDialog
          residentId={dialog.row.residentId}
          residentName={dialog.row.residentName}
          facilityId={dialog.row.facilityId}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
